import type { AxiosInstance } from 'axios'
import type {
  PaymentBody,
  PaymentStatusType,
  PaymentType,
  PromoCodeBody,
  PromoCodeValidationResponse,
  TariffType,
} from 'src/types/payments'
import type { SubscriptionType } from 'src/types/subscriptions'
import sortBy from 'lodash/sortBy'
import { differenceInDays } from 'date-fns'

export class PaymentsApi {
  api: AxiosInstance

  urls = {
    tariffs: () => 'payments/tariff/' as const,
    createYookassaPayment: () => 'payments/yookassa/create/' as const,
    createTkassaPayment: () => 'payments/tkassa/create/' as const,
    paymentStatus: (paymentId: PaymentType['id']) => `payments/yookassa/status/${paymentId}/`,
    subscriptions: () => 'payments/subscriptions/',
    promoCodeValidate: () => 'promo-codes/validate/',
    finishOnboarding: () => 'payments/finish_onboarding/',
    cancelSubscriptionAutoRenewal: () => 'payments/subscriptions/autorenewal/cancel/',
  }

  constructor (api: AxiosInstance) {
    this.api = api
  }

  getTariffs = async (): Promise<TariffType[]> => {
    const response = await this.api.get<TariffType[]>(this.urls.tariffs())
    return sortBy(response.data, (o: TariffType) => o.duration)
  }

  createYookassaPayment = async (body: PaymentBody): Promise<PaymentType> => {
    const response = await this.api.post<PaymentType>(
      this.urls.createYookassaPayment(), {}, { params: body }
    )
    return response.data
  }

  createTkassaPayment = async (body: PaymentBody): Promise<PaymentType> => {
    const response = await this.api.post<PaymentType>(
      this.urls.createTkassaPayment(), {}, { params: body }
    )
    return response.data
  }

  cancelSubscriptionAutoRenewal = async (subscriptionId: SubscriptionType['id']): Promise<void> => {
    await this.api.post(
      this.urls.cancelSubscriptionAutoRenewal(), { subscription_id: subscriptionId }
    )
  }

  getSubscriptions = async (): Promise<SubscriptionType[]> => {
    const response = await this.api.get<SubscriptionType[]>(this.urls.subscriptions())

    const now = new Date()
    return response.data.map((subscription: SubscriptionType) => {
      return { ...subscription, days: differenceInDays(subscription.end_date, now) }
    })
  }

  getPaymentStatus = async (paymentId: PaymentType['id']): Promise<PaymentStatusType> => {
    const response = await this.api.get<PaymentStatusType>(
      this.urls.paymentStatus(paymentId)
    )
    return response.data
  }

  validatePromoCode = async (body: PromoCodeBody): Promise<PromoCodeValidationResponse> => {
    const response = await this.api.get<PromoCodeValidationResponse>(
      this.urls.promoCodeValidate(), { params: { ...body, _suppressNotify: true } }
    )
    return response.data
  }

  finishOnboarding = async () => {
    return this.api.post(this.urls.finishOnboarding())
  }
}
